import React, { useState, useEffect } from 'react';
import { CheckCircle2, Loader2, Key, Copy, Landmark, FlaskConical } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { logUsageEvent } from '../../src/lib/logger';
import LoadingSpinner from '../../src/components/LoadingSpinner';

// Credenciais do gateway de pagamento da PRÓPRIA Vigor Energy (hoje: Asaas).
// Desde 06/08/2026 esta conta NÃO emite o boleto único: quem emite é a empresa
// parceira, na conta dela (Config > Empresas Parceiras > Credenciais Asaas), e a
// parte da Vigor chega aqui por SPLIT no momento do pagamento — o walletId abaixo
// é o destino desse split, não uma referência qualquer: sem ele a emissão é
// recusada. O que entra por split inclui a parte da fatura da Equatorial embutida
// no boleto único, de onde sai o pagamento automático da Equatorial via /bill —
// que continua saindo desta conta, com esta chave de API.
//
// Produção e sandbox são contas Asaas SEPARADAS e coexistem: cada aba abaixo
// edita a sua própria linha (banco + ambiente). Antes existia uma linha só com
// um seletor de ambiente, o que obrigava a derrubar a emissão real para testar
// qualquer coisa — na prática todo teste virava cobrança de verdade.

interface StatusAmbiente {
  configurado: boolean;
  webhook_configurado: boolean;
  ambiente: string;
  wallet_id_proprio: string | null;
}

type Ambiente = 'producao' | 'sandbox';

const VigorGatewayAsaasView: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [ambientes, setAmbientes] = useState<Record<Ambiente, StatusAmbiente> | null>(null);
  const [aba, setAba] = useState<Ambiente>('producao');
  const [apiKey, setApiKey] = useState('');
  const [walletIdProprio, setWalletIdProprio] = useState('');
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [novoToken, setNovoToken] = useState<string | null>(null);

  const webhookUrl = `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/asaas-webhook`;
  const status = ambientes?.[aba] ?? null;
  const isSandbox = aba === 'sandbox';

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-vigor-gateway-banco', {
        body: { action: 'getStatus' },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setAmbientes(data.ambientes);
    } catch {
      addToast('Erro ao carregar status das credenciais.', 'error');
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  // Troca de aba = outro conjunto de credenciais: limpa o que estava digitado
  // para não salvar a chave de produção dentro do sandbox por descuido.
  //
  // Depende SÓ de `aba`, de propósito. Com `ambientes` na lista, todo loadStatus()
  // limpava o token recém-gerado — e como ele só é exibível uma vez, ficava
  // impossível copiá-lo: era gerado no banco e apagado da tela no mesmo instante.
  useEffect(() => {
    setApiKey('');
    setNovoToken(null);
  }, [aba]);

  // O walletId exibido acompanha o que está salvo no ambiente selecionado.
  useEffect(() => {
    setWalletIdProprio(ambientes?.[aba]?.wallet_id_proprio || '');
  }, [aba, ambientes]);

  const handleSaveCredenciais = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      addToast('Informe a chave de API do Asaas.', 'error');
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-vigor-gateway-banco', {
        body: {
          action: 'setCredenciais',
          api_key: apiKey.trim(),
          wallet_id_proprio: walletIdProprio.trim() || null,
          ambiente: aba,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      addToast(`Credenciais de ${isSandbox ? 'sandbox' : 'produção'} salvas com sucesso.`, 'success');
      logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'conta_asaas_vigor', acao: 'salvar_credenciais_asaas', origem: 'acao' });
      setApiKey('');
      loadStatus();
    } catch (err: any) {
      addToast('Erro ao salvar credenciais: ' + (err.message || 'Erro desconhecido'), 'error', 7000);
    } finally {
      setSaving(false);
    }
  };

  const handleRotateToken = async () => {
    setRotating(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-vigor-gateway-banco', {
        body: { action: 'rotateWebhookToken', ambiente: aba },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setNovoToken(data.token);
      logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'conta_asaas_vigor', acao: 'rotacionar_token_webhook', origem: 'acao' });
      loadStatus();
    } catch (err: any) {
      addToast('Erro ao gerar token: ' + (err.message || 'Erro desconhecido'), 'error', 7000);
    } finally {
      setRotating(false);
    }
  };

  const copyToClipboard = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    addToast(`${label} copiado.`, 'success');
  };

  if (loadingStatus) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  const abaClasse = (alvo: Ambiente) => {
    const ativo = aba === alvo;
    if (!ativo) return 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border-transparent';
    return alvo === 'sandbox'
      ? 'text-amber-600 dark:text-amber-400 border-amber-500'
      : 'text-primary border-primary';
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h2 className="text-2xl font-bold font-poppins text-gray-900 dark:text-white flex items-center gap-2">
          <Landmark size={24} className="text-primary" />
          Conta Asaas — Vigor Energy
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Conta que <strong>recebe por split</strong> a parte da Vigor nos boletos únicos emitidos pelas empresas
          parceiras, e de onde sai o pagamento automático da Equatorial. Quem emite a cobrança é cada parceira, com a
          chave dela (Config &gt; Empresas Parceiras &gt; Credenciais Asaas).
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        {/* Cada aba edita uma conta Asaas diferente — as duas convivem. */}
        <div className="flex border-b border-gray-100 dark:border-gray-700 px-2">
          <button
            onClick={() => setAba('producao')}
            className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-1.5 ${abaClasse('producao')}`}
          >
            <Landmark size={15} /> Produção
            {ambientes?.producao.configurado && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
          </button>
          <button
            onClick={() => setAba('sandbox')}
            className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-1.5 ${abaClasse('sandbox')}`}
          >
            <FlaskConical size={15} /> Sandbox
            {ambientes?.sandbox.configurado && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
          </button>
        </div>

        <div className="p-6 space-y-6">
          {isSandbox && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
              <p className="text-xs text-amber-800 dark:text-amber-300">
                <strong>Ambiente de teste.</strong> Cobranças emitidas aqui não movimentam dinheiro e ficam
                marcadas como sandbox — só aparecem no card "Sandbox" da tela de Faturas, e nunca entram em
                somatórios, inadimplência ou exportações. A chave começa com <code>$aact_hmlg_</code>.
              </p>
            </div>
          )}

          <div className="flex items-center gap-4 text-sm">
            <span className={`flex items-center gap-1.5 ${status?.configurado ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
              <CheckCircle2 size={14} /> Chave de API {status?.configurado ? 'configurada' : 'não configurada'}
            </span>
            <span className={`flex items-center gap-1.5 ${status?.webhook_configurado ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
              <CheckCircle2 size={14} /> Webhook {status?.webhook_configurado ? 'configurado' : 'não configurado'}
            </span>
          </div>

          <form onSubmit={handleSaveCredenciais} className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Chave de API (access_token) <span className="text-red-500">*</span></label>
              <input
                type="password"
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                placeholder={status?.configurado ? '•••••••• (preencha para substituir)' : (isSandbox ? '$aact_hmlg_...' : '$aact_prod_...')}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                WalletId da própria Vigor Energy (destino do split) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                value={walletIdProprio}
                onChange={e => setWalletIdProprio(e.target.value)}
              />
            </div>
            <button type="submit" disabled={saving}
              className={`w-full py-2.5 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 ${isSandbox ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20' : 'bg-primary hover:bg-primary/90 shadow-primary/20'}`}>
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Key size={18} />}
              Salvar credenciais de {isSandbox ? 'sandbox' : 'produção'}
            </button>
          </form>

          <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Configure esta URL no painel do Asaas desta conta em <strong>Webhooks de Pague Contas</strong> (eventos
                BILL_*), com o token gerado abaixo — é o que confirma o pagamento automático da Equatorial. Os eventos
                PAYMENT_* das cobranças vêm do webhook da <strong>conta da empresa parceira</strong>, que é quem emite
                (Config &gt; Empresas Parceiras &gt; Credenciais Asaas). A URL é a mesma para todos: quem diz de onde
                veio o evento é o token.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input readOnly value={webhookUrl}
                className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-lg text-xs" />
              <button onClick={() => copyToClipboard(webhookUrl, 'URL do webhook')}
                className="p-2 text-gray-400 hover:text-primary transition-colors" title="Copiar URL">
                <Copy size={16} />
              </button>
            </div>
            <button onClick={handleRotateToken} disabled={rotating}
              className="w-full py-2 border-2 border-dashed border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 rounded-xl hover:border-primary hover:text-primary transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              {rotating ? <Loader2 size={16} className="animate-spin" /> : <Key size={16} />}
              {status?.webhook_configurado ? 'Rotacionar token de webhook' : 'Gerar token de webhook'}
              {isSandbox && ' (sandbox)'}
            </button>

            {novoToken && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 space-y-2">
                <p className="text-xs text-yellow-800 dark:text-yellow-300 font-medium">
                  Copie agora — este valor não será mostrado novamente. Cole em "Token de acesso" ao cadastrar os DOIS
                  webhooks no painel do Asaas de {isSandbox ? 'sandbox' : 'produção'}, junto com a URL acima.
                </p>
                <div className="flex items-center gap-2">
                  <input readOnly value={novoToken}
                    className="flex-1 px-3 py-2 border border-yellow-300 dark:border-yellow-700 bg-white dark:bg-gray-800 dark:text-gray-200 rounded-lg text-xs font-mono" />
                  <button onClick={() => copyToClipboard(novoToken, 'Token')}
                    className="p-2 text-yellow-700 hover:text-yellow-900 transition-colors" title="Copiar token">
                    <Copy size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VigorGatewayAsaasView;
